import { FC, useEffect, useState } from 'react'
import styled, { createGlobalStyle } from 'styled-components'
import { validFor } from '@/theme/validFor'

type StickyBottomBarProps = {
  // The page's own button this bar stands in for; the bar hides while that
  // button is on screen, so the same action is never shown twice.
  inlineButton: HTMLElement | null
} & Rest

// Mobile bar pinned to the bottom of the screen. It reserves its own height
// at the end of the page, so it never covers the footer, and lifts the
// scroll-to-top button above itself.
const StickyBottomBar: FC<StickyBottomBarProps> = ({
  inlineButton,
  children,
  ...rest
}) => {
  const [isInlineButtonVisible, setIsInlineButtonVisible] = useState(true)
  const [bar, setBar] = useState<HTMLDivElement>(null)
  const [height, setHeight] = useState(0)

  useEffect(() => {
    if (!inlineButton || typeof IntersectionObserver === 'undefined') {
      return
    }
    const observer = new IntersectionObserver(([entry]) =>
      setIsInlineButtonVisible(entry.isIntersecting)
    )
    observer.observe(inlineButton)
    return () => observer.disconnect()
  }, [inlineButton])

  useEffect(() => {
    if (!bar) {
      return
    }
    const update = () => setHeight(bar.offsetHeight)
    update()
    if (typeof ResizeObserver === 'undefined') {
      return
    }
    const observer = new ResizeObserver(update)
    observer.observe(bar)
    return () => observer.disconnect()
  }, [bar])

  const visible = !!inlineButton && !isInlineButtonVisible

  return (
    <>
      <StickyBottomBarSpace height={height} />
      <StyledBar
        ref={setBar}
        visible={visible}
        aria-hidden={!visible}
        {...rest}
      >
        {children}
      </StyledBar>
    </>
  )
}

export default StickyBottomBar

const StickyBottomBarSpace = createGlobalStyle`
  ${validFor.mobile`
    body {
      padding-bottom: ${({ height }) => height}px;
    }

    body [data-scroll-to-top] {
      bottom: ${({ height }) => height + 10}px;
    }
  `}
`

const StyledBar = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99;
  padding: 14px 16px calc(14px + env(safe-area-inset-bottom));
  box-sizing: border-box;
  background-color: ${(props) => props.theme.colors.background};
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.12);
  transform: translateY(${(props) => (props.visible ? '0' : '110%')});
  visibility: ${(props) => (props.visible ? 'visible' : 'hidden')};
  transition: transform 0.2s ease-out, visibility 0.2s;
`
